import { useMemo } from 'react';
import type {
  CreateMapArrowInput,
  CreateMapObjectInput,
  ListMapArrowsResponse,
  ListMapObjectsResponse,
  MapArrowDTO,
  MapObjectDTO,
  OperationPlanDTO,
  PatchMapArrowInput,
  PatchMapObjectInput,
  PatchOperationPlanInput,
  PatchWarPlanInput,
  WarPlanDTO,
} from '@warplan/shared';
import { useActiveMode } from '../state/active-mode-store.js';
import { useSandboxStore, type SandboxSlice } from '../state/sandbox-store.js';
import { getMapFlavour, isSandbox } from './view-mode.js';
import {
  useMapObjects,
  useCreateMapObject,
  usePatchMapObject,
  useDeleteMapObject,
} from './map-objects-api.js';
import {
  useMapArrows,
  useCreateMapArrow,
  usePatchMapArrow,
  useDeleteMapArrow,
} from './map-arrows-api.js';
import { useWarPlan, useUpdateWarPlan } from './war-plan-api.js';
import { useOperationPlan, useUpdateOperationPlan } from './operation-plan-api.js';

/**
 * Data-source aiguillage : selon `viewMode`, chaque hook retourne soit les
 * données live (React Query / REST) soit l'état local du sandbox-store.
 *
 * La signature retournée mime celle des hooks API d'origine (`{ data, isLoading,
 * isError, mutate, isPending }`) pour que les composants restent identiques.
 *
 * Side-effect important : tous les hooks API sont toujours appelés (règles
 * des hooks React) — mais en sandbox, leurs résultats sont ignorés. Cela
 * conserve donc le cache live à jour pour un retour rapide vers le mode synchro.
 */

// ---------------------------------------------------------------------------
// Objects
// ---------------------------------------------------------------------------

export function useObjectsSource(): {
  data: ListMapObjectsResponse | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const viewMode = useActiveMode((s) => s.viewMode);
  const flavour = getMapFlavour(viewMode);
  const live = useMapObjects();
  const sandboxSlice = useSandboxStore((s) => (flavour === 'operation' ? s.operation : s.planner));

  if (isSandbox(viewMode)) {
    return {
      data: { objects: sandboxSlice.objects },
      isLoading: false,
      isError: false,
    };
  }
  return {
    data: live.data,
    isLoading: live.isLoading,
    isError: live.isError,
  };
}

export function useCreateObjectSource(): {
  mutate: (input: CreateMapObjectInput) => void;
  isPending: boolean;
} {
  const viewMode = useActiveMode((s) => s.viewMode);
  const flavour = getMapFlavour(viewMode);
  const sandboxCreate = useSandboxStore((s) => s.createObject);
  const live = useCreateMapObject();

  return useMemo(() => {
    if (isSandbox(viewMode)) {
      return {
        mutate: (input) => {
          sandboxCreate(flavour, input);
        },
        isPending: false,
      };
    }
    return { mutate: (input) => live.mutate(input), isPending: live.isPending };
  }, [viewMode, flavour, sandboxCreate, live]);
}

export function usePatchObjectSource(): {
  mutate: (vars: { id: string; patch: PatchMapObjectInput }) => void;
  isPending: boolean;
} {
  const viewMode = useActiveMode((s) => s.viewMode);
  const flavour = getMapFlavour(viewMode);
  const sandboxPatch = useSandboxStore((s) => s.patchObject);
  const live = usePatchMapObject();

  return useMemo(() => {
    if (isSandbox(viewMode)) {
      return {
        mutate: ({ id, patch }) => {
          sandboxPatch(flavour, id, patch);
        },
        isPending: false,
      };
    }
    return {
      mutate: (vars) => live.mutate(vars),
      isPending: live.isPending,
    };
  }, [viewMode, flavour, sandboxPatch, live]);
}

export function useDeleteObjectSource(): {
  mutate: (id: string) => void;
  isPending: boolean;
} {
  const viewMode = useActiveMode((s) => s.viewMode);
  const flavour = getMapFlavour(viewMode);
  const sandboxDelete = useSandboxStore((s) => s.deleteObject);
  const live = useDeleteMapObject();

  return useMemo(() => {
    if (isSandbox(viewMode)) {
      return {
        mutate: (id) => {
          sandboxDelete(flavour, id);
        },
        isPending: false,
      };
    }
    return { mutate: (id) => live.mutate(id), isPending: live.isPending };
  }, [viewMode, flavour, sandboxDelete, live]);
}

// ---------------------------------------------------------------------------
// Arrows
// ---------------------------------------------------------------------------

export function useArrowsSource(): {
  data: ListMapArrowsResponse | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const viewMode = useActiveMode((s) => s.viewMode);
  const flavour = getMapFlavour(viewMode);
  const live = useMapArrows();
  const sandboxSlice = useSandboxStore((s) => (flavour === 'operation' ? s.operation : s.planner));

  if (isSandbox(viewMode)) {
    return {
      data: { arrows: sandboxSlice.arrows },
      isLoading: false,
      isError: false,
    };
  }
  return {
    data: live.data,
    isLoading: live.isLoading,
    isError: live.isError,
  };
}

export function useCreateArrowSource(): {
  mutate: (input: CreateMapArrowInput) => void;
  isPending: boolean;
} {
  const viewMode = useActiveMode((s) => s.viewMode);
  const flavour = getMapFlavour(viewMode);
  const sandboxCreate = useSandboxStore((s) => s.createArrow);
  const live = useCreateMapArrow();

  return useMemo(() => {
    if (isSandbox(viewMode)) {
      return {
        mutate: (input) => {
          sandboxCreate(flavour, input);
        },
        isPending: false,
      };
    }
    return { mutate: (input) => live.mutate(input), isPending: live.isPending };
  }, [viewMode, flavour, sandboxCreate, live]);
}

export function usePatchArrowSource(): {
  mutate: (vars: { id: string; patch: PatchMapArrowInput }) => void;
  isPending: boolean;
} {
  const viewMode = useActiveMode((s) => s.viewMode);
  const flavour = getMapFlavour(viewMode);
  const sandboxPatch = useSandboxStore((s) => s.patchArrow);
  const live = usePatchMapArrow();

  return useMemo(() => {
    if (isSandbox(viewMode)) {
      return {
        mutate: ({ id, patch }) => {
          sandboxPatch(flavour, id, patch);
        },
        isPending: false,
      };
    }
    return { mutate: (vars) => live.mutate(vars), isPending: live.isPending };
  }, [viewMode, flavour, sandboxPatch, live]);
}

export function useDeleteArrowSource(): {
  mutate: (id: string) => void;
  isPending: boolean;
} {
  const viewMode = useActiveMode((s) => s.viewMode);
  const flavour = getMapFlavour(viewMode);
  const sandboxDelete = useSandboxStore((s) => s.deleteArrow);
  const live = useDeleteMapArrow();

  return useMemo(() => {
    if (isSandbox(viewMode)) {
      return {
        mutate: (id) => {
          sandboxDelete(flavour, id);
        },
        isPending: false,
      };
    }
    return { mutate: (id) => live.mutate(id), isPending: live.isPending };
  }, [viewMode, flavour, sandboxDelete, live]);
}

// ---------------------------------------------------------------------------
// War-plan
// ---------------------------------------------------------------------------

export function useWarPlanSource(): {
  data: WarPlanDTO | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const viewMode = useActiveMode((s) => s.viewMode);
  const live = useWarPlan();
  const sandboxPlan = useSandboxStore((s) => s.planner.warPlan);

  if (isSandbox(viewMode)) {
    return { data: sandboxPlan, isLoading: false, isError: false };
  }
  return { data: live.data, isLoading: live.isLoading, isError: live.isError };
}

export function useUpdateWarPlanSource(): {
  mutate: (patch: PatchWarPlanInput) => void;
  isPending: boolean;
} {
  const viewMode = useActiveMode((s) => s.viewMode);
  const sandboxPatch = useSandboxStore((s) => s.patchWarPlan);
  const live = useUpdateWarPlan();

  return useMemo(() => {
    if (isSandbox(viewMode)) {
      return {
        mutate: (patch) => {
          sandboxPatch(patch);
        },
        isPending: false,
      };
    }
    return { mutate: (patch) => live.mutate(patch), isPending: live.isPending };
  }, [viewMode, sandboxPatch, live]);
}

// ---------------------------------------------------------------------------
// Operation-plan
// ---------------------------------------------------------------------------

export function useOperationPlanSource(): {
  data: OperationPlanDTO | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const viewMode = useActiveMode((s) => s.viewMode);
  const live = useOperationPlan();
  const sandboxPlan = useSandboxStore((s) => s.operation.operationPlan);

  if (isSandbox(viewMode)) {
    return { data: sandboxPlan, isLoading: false, isError: false };
  }
  return { data: live.data, isLoading: live.isLoading, isError: live.isError };
}

export function useUpdateOperationPlanSource(): {
  mutate: (patch: PatchOperationPlanInput) => void;
  isPending: boolean;
} {
  const viewMode = useActiveMode((s) => s.viewMode);
  const sandboxPatch = useSandboxStore((s) => s.patchOperationPlan);
  const live = useUpdateOperationPlan();

  return useMemo(() => {
    if (isSandbox(viewMode)) {
      return {
        mutate: (patch) => {
          sandboxPatch(patch);
        },
        isPending: false,
      };
    }
    return { mutate: (patch) => live.mutate(patch), isPending: live.isPending };
  }, [viewMode, sandboxPatch, live]);
}

// ---------------------------------------------------------------------------
// Convenience: snapshot live state to clone it into the sandbox
// ---------------------------------------------------------------------------

/**
 * Returns a snapshot of the live data for the given flavour, ready to be
 * passed to `useSandboxStore.replaceFlavour(flavour, snapshot)`.
 */
export function buildSnapshotFromLive(
  flavour: 'planner' | 'operation',
  liveObjects: ReadonlyArray<MapObjectDTO>,
  liveArrows: ReadonlyArray<MapArrowDTO>,
  liveWarPlan: WarPlanDTO | null,
  liveOperationPlan: OperationPlanDTO | null,
): SandboxSlice {
  const objects = liveObjects.filter((o) => o.mode === flavour);
  const arrows = liveArrows.filter((a) => a.mode === flavour);
  // Default plans are pulled from the existing sandbox-store factory.
  // Falling back keeps the slice complete even when the live plan failed to load.
  const ts = new Date().toISOString();
  return {
    objects: objects.map((o) => ({ ...o, mode: flavour })),
    arrows: arrows.map((a) => ({ ...a, mode: flavour })),
    warPlan: liveWarPlan
      ? { ...liveWarPlan, updatedAt: ts }
      : { title: 'Sandbox War-Plan', warStyle: 'balanced', opFrequency: 'medium', stockManagement: 'balanced_use', griefingRisk: 'medium', updatedAt: ts, updatedBy: null },
    operationPlan: liveOperationPlan
      ? { ...liveOperationPlan, updatedAt: ts }
      : { warNumber: 0, title: "Sandbox d'opération", date: '', objective: '', objectiveDefense: '', objectiveType: 'attack', priorities: [], updatedAt: ts, updatedBy: null },
  };
}
